import React from "react";
import {
  Calendar, Phone, MessageSquare, ShieldCheck, Activity, Brain,
  Stethoscope, Zap, Clock, Sparkles,
} from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  AgentCard, ParallelGrid, FeatureCallout, LiveFeed,
  SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogLeenaAiScheduleOptimizer = () => (
  <RichBlogLayout
    category="Schedule Optimization"
    eyebrow="Schedule Optimizer"
    title="Every Open Chair, Filled at Once: Inside Leena, the AI Schedule Optimizer"
    subtitle="Cancellations, no-shows, and same-day gaps stop being firefights. Leena reasons through every opening at the same time."
    author="Asha Team"
    date="May 2, 2026"
    readTime="8 min read"
  >
    <Prose>
      <p>
        It's 9:42 AM. The schedule looked clean an hour ago. Then a hygienist
        comes back from her morning huddle: a noon prophy just cancelled. Two
        minutes later, the front desk pulls a confirmation list and finds a
        no-show at 1:30 PM. By 10:15, a third call comes in — a crown patient
        rescheduling from 3:00 to next Tuesday.
      </p>
      <p>
        Within thirty minutes the practice has lost three slots, two providers
        are about to be idle, and there is no realistic way for the front desk
        to call down the wait list, screen for insurance, match procedures, and
        fill all three openings while also answering the phone, checking
        patients in, and helping the hygienist with a balance question.
      </p>
      <p>
        Production quietly slips out the back door. This is how revenue
        actually leaks at most practices — not in big chunks, but in 30-minute
        gaps that nobody had time to fill.
      </p>
    </Prose>

    <AgentCard
      name="Leena"
      role="AI Schedule Optimizer · Recover Production"
      initial="L"
      description="Leena turns every cancellation, no-show, and open chair into filled production. She works every opening at the same time — pulling from wait lists, reaching out to overdue patients, offering same-day slots — and reasons through insurance, provider, procedure, and timing for each one."
      channels={["Wait list outreach", "SMS + voice", "Provider-aware", "Real-time reshuffle"]}
    />

    <Prose>
      <h2>What a gap in the chair actually costs</h2>
      <p>
        The dollar value of an empty operatory hour depends on the procedure,
        but a useful directional number is $300–$600 per provider hour at a
        general practice and meaningfully higher at specialty offices. Three
        unfilled slots in a morning is a four-figure hole in the day's
        production. Five days a week, fifty weeks a year, those minutes are
        the difference between a healthy P&L and a stressed one.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 100, suffix: "+", label: "Open slots worked at the same time" },
        { value: 5, label: "Variables reasoned per slot" },
        { value: 0, label: "Voicemails left untracked" },
        { value: 24, suffix: "/7", label: "Watching the schedule, never off shift" },
      ]}
    />

    <Prose>
      <p>
        The traditional answer to schedule gaps is heroics. Someone on the
        front desk picks up the wait list and starts calling. They leave
        voicemails. They miss the next gap that opens while they're on the
        phone. They fill one slot — sometimes — and the rest of the day's
        openings just decay into lost production.
      </p>
      <p>
        Leena replaces the heroics with breadth. She works every gap at once.
      </p>

      <h2>Calling down a list vs. working every gap at once</h2>
    </Prose>

    <BeforeAfter
      beforeTitle="Front desk calling down a list"
      afterTitle="Leena · every gap worked simultaneously"
      beforeItems={[
        "One staffer working one slot at a time",
        "Voicemails left with no follow-up cadence",
        "Each call requires manual insurance, provider, and procedure check",
        "By the time one slot is filled, two more have opened",
      ]}
      afterItems={[
        "Every open slot is being worked simultaneously",
        "SMS and voice run together; pivots automatically when one fails",
        "Insurance, provider, procedure, and timing reasoned per opening",
        "When a new gap opens, Leena absorbs it instantly into the queue",
      ]}
    />

    <Prose>
      <h2>What Leena reasons through, per slot</h2>
      <p>
        This is the part that distinguishes Leena from a "fill the gap" blast
        tool. For every opening, she's not just looking for someone with a
        pulse and a free hour. She's solving a small operational puzzle in real
        time.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "Detects the opening the moment it appears",
          body:
            "A cancellation, a no-show, a reschedule, a stalled confirmation — Leena sees the gap the second it forms in the schedule.",
        },
        {
          title: "Reasons through the constraints",
          body:
            "She looks at insurance status, provider requirements, procedure type, chair availability, and timing — all at once — to figure out what the slot actually needs.",
        },
        {
          title: "Identifies the best candidates",
          body:
            "She pulls from the wait list, overdue recalls, same-day opportunities, and treatment-plan follow-ups — ranking by fit, not just by who picks up first.",
        },
        {
          title: "Reaches out across SMS and voice at the same time",
          body:
            "Multiple candidates contacted simultaneously, on the channel each one prefers. No one is sitting on hold; nobody is being ignored.",
        },
        {
          title: "Adapts to every reply",
          body:
            "If a patient doesn't respond, she pivots. If they need a different time, she adjusts. If they raise a known objection — cost, scheduling, anxiety — she factors it in and responds in context.",
        },
        {
          title: "Books and reshuffles when needed",
          body:
            "Leena drops the booking directly into the schedule — and if a smarter rearrangement of the day fills the slot better, she'll proactively reshuffle.",
        },
      ]}
    />

    <PullQuote>
      A 1:30 PM crown seat needs a different patient than a noon prophy.
      Leena reasons through both at the same time, and doesn't trade one off
      against the other.
    </PullQuote>

    <Prose>
      <h2>When multiple cancellations hit at once</h2>
      <p>
        The truly painful days aren't the single-cancellation days. They're the
        ones where three or four gaps open within an hour of each other —
        often at peak production hours. That can put thousands of dollars at
        risk in minutes. The front desk's only realistic move is to triage:
        try to fill the most expensive slot and let the rest go.
      </p>
      <p>
        Leena doesn't triage that way. She works all of them at once.
      </p>
    </Prose>

    <ParallelGrid
      title="10:42 AM · three concurrent gap recoveries"
      columns={3}
      items={[
        { title: "Op 2 · 12:00 PM", subtitle: "Prophy · Lara · 60 min · 6 SMS sent", icon: MessageSquare, status: "active" },
        { title: "Op 4 · 1:30 PM", subtitle: "Crown seat · Dr. Patel · 4 voice calls", icon: Phone, status: "active" },
        { title: "Op 1 · 3:00 PM", subtitle: "Filling · Dr. Kim · matching insurance", icon: ShieldCheck, status: "active" },
        { title: "Wait list scan", subtitle: "47 candidates ranked by fit", icon: Brain, status: "done" },
        { title: "Same-day outreach", subtitle: "9 overdue patients texted", icon: Activity, status: "done" },
        { title: "Reshuffle proposal", subtitle: "Move 4pm hygiene → 1:30pm", icon: Calendar, status: "active" },
      ]}
    />

    <Prose>
      <p>
        Each card above is its own decision tree, running independently. The
        12:00 prophy needs a different approach than the 1:30 crown seat. The
        hygiene gap can be backfilled from a recall list. The crown seat needs
        a patient with the right insurance, comfortable with that provider,
        able to come in on short notice. Leena handles all three threads at the
        same time, without trading one off against another.
      </p>

      <h2>Outreach is intentional — not a blast</h2>
      <p>
        Each message Leena sends is shaped by the opening and the candidate.
        She doesn't send 50 patients the same "we have an opening today" text
        and let them race to claim it. That feels spammy and damages future
        engagement. She sends each candidate a message that respects their
        history and matches the slot to them.
      </p>
    </Prose>

    <LiveFeed
      title="Leena · live recovery activity"
      items={[
        { time: "10:42", label: "Cancellation detected · Op 2 · 12:00 PM prophy", detail: "Leena began outreach across 6 wait-list candidates", status: "done" },
        { time: "10:43", label: "Reshuffle proposed · move 4 PM hygiene → 1:30 PM", detail: "Patient confirmed via SMS · approved", status: "done" },
        { time: "10:46", label: "Op 1 · 3:00 PM filling · 4 candidates contacted", detail: "Filtered by insurance + procedure type", status: "active" },
        { time: "10:48", label: "Voice call answered · James P. · same-day prophy", detail: "Booked Op 2 · 12:00 PM · confirmation sent", status: "done" },
        { time: "10:51", label: "Insurance verification triggered for new bookings", detail: "Auto-handed off to Amanda", status: "active" },
        { time: "10:54", label: "Op 1 · 3:00 PM filled by Sofia G.", detail: "Filling on Delta PPO · Dr. Kim confirmed", status: "done" },
      ]}
    />

    <Prose>
      <h2>Why parallel reasoning is the unlock</h2>
      <p>
        It's tempting to think of this as "faster than a person." That undersells
        it. The unlock isn't speed; it's that humans literally cannot reason
        through five operational variables across multiple slots at the same
        time. The front desk has to serialize. Leena doesn't.
      </p>
      <p>
        That's why the math works out so differently. A human team caps out at
        one or two recoveries per hour. Leena recovers as many as the gaps and
        the candidate pool allow — without dropping the ball on any of them.
      </p>
    </Prose>

    <FeatureCallout
      stat="Reactive → Ahead"
      label="From firefighting cancellations to staying ahead of them"
      description="Leena works every open chair simultaneously, reasoning through insurance, provider, procedure, and timing — recovering production in minutes, not at the end of the day."
      icon={Zap}
    />

    <Prose>
      <h2>How the day feels different at the front desk</h2>
      <p>
        With Leena handling gap recovery, the front desk stops being the
        firefighting unit. The cascade — cancellation, scramble, voicemails,
        the next gap, more scrambling — just doesn't happen. They focus on the
        patients in the room. The schedule fills itself off to the side.
      </p>
      <p>
        And operationally, the day looks different at the close. Production
        recovery becomes a built-in expectation, not a heroic outcome that
        requires the right person to be at the desk at the right minute.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        Empty chairs are the single most consistent source of revenue leakage
        in dentistry — and the one that's hardest to fix manually. Leena solves
        it the only way it actually scales: every gap, every candidate, every
        constraint, worked at the same time, every day.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogLeenaAiScheduleOptimizer;
